// File: frontend/src/components/ui/MobileCardList.js
//
// A TABLE'S ROWS, AS CARDS, BELOW THE BREAKPOINT.
//
// WHY THIS IS ITS OWN PRIMITIVE
//   DataTable already knew how to swap a table for a list of cards on a phone,
//   but the knowledge lived inside DataTable's render — so the ONE page that
//   needed the same treatment outside DataTable (Hotspot Customers, which had
//   grown its own `isMobile ? cards : table` branch) reimplemented the card
//   shell, the loading state, the empty state and the keyboard affordance a
//   second time, and the two drifted. This file is the single answer to "how
//   does a row look when it is not a row", and DataTable is now its first
//   caller rather than its owner.
//
// WHAT IT OWNS AND WHAT IT DOES NOT
//   It owns PRESENTATION: the card shell, the spacing, the loading skeletons,
//   the empty state, the tap-target floor. It owns no BEHAVIOUR — the caller
//   decides what a card does when it is pressed and supplies those props via
//   `getItemProps`. That split is deliberate: DataTable's rows and DataTable's
//   cards must open the same thing in the same way, and that equivalence
//   belongs in DataTable, next to `onRowClick`, not in two places.
//
// TWO SHAPES, BECAUSE TWO SURFACES ALREADY NEEDED THEM
//   variant="card" — separated, individually bordered cards. This is what a
//     list sitting on a page or in a dialog wants, and it is what DataTable's
//     original card branch drew, so existing callers are unchanged.
//   variant="row"  — flush, divider-separated rows. This is what a list already
//     inside a bordered Paper wants; a second border inside a border is noise.
//
//   <MobileCardList
//     items={rows}
//     getKey={(r) => r.id}
//     renderItem={(r) => <CustomerCard row={r} />}
//     getItemProps={(r, i) => ({ role: "button", onClick: () => open(r, i) })}
//   />

import React from "react";
import { Box, Stack, Skeleton, useTheme } from "@mui/material";
import EmptyState from "./EmptyState";
import { MIN_TAP } from "./tokens";

export default function MobileCardList({
  items = [],
  getKey,
  renderItem,
  getItemProps,
  loading = false,
  skeletonCount = 3,
  variant = "card",
  emptyState,
  emptyTitle = "Nothing here yet",
  emptyDescription,
  sx,
  ...rest
}) {
  const theme = useTheme();
  const flush = variant === "row";

  const shellSx = flush
    ? {
        p: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-of-type": { borderBottom: "none" },
      }
    : {
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
      };

  if (loading) {
    return (
      <Stack spacing={flush ? 0 : 1} sx={sx} {...rest}>
        {Array.from({ length: skeletonCount }, (_, i) => (
          <Box key={i} sx={shellSx}>
            <Skeleton width="55%" height={20} />
            <Skeleton width="35%" height={16} />
          </Box>
        ))}
      </Stack>
    );
  }

  if (!items || items.length === 0) {
    return emptyState || <EmptyState title={emptyTitle} description={emptyDescription} dense />;
  }

  return (
    <Stack spacing={flush ? 0 : 1} sx={sx} {...rest}>
      {items.map((item, i) => {
        const props = (getItemProps ? getItemProps(item, i) : null) || {};
        // A card is only a control when the caller made it one. An inert card
        // must not claim a tab stop, and must not grow a hover state that
        // promises something will happen.
        const interactive = Boolean(props.onClick || props.onKeyDown || props.role === "button");
        return (
          <Box
            key={getKey ? getKey(item, i) : i}
            {...props}
            sx={{
              ...shellSx,
              minWidth: 0,
              // §13.4 — a whole card is the tap target, so the floor applies to
              // the card and not to some affordance drawn inside it.
              ...(interactive ? { minHeight: MIN_TAP } : null),
              cursor: interactive ? "pointer" : "default",
              "&:hover": interactive ? { bgcolor: "action.hover" } : undefined,
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: flush ? -2 : 2,
              },
              ...props.sx,
            }}
          >
            {renderItem ? renderItem(item, i) : null}
          </Box>
        );
      })}
    </Stack>
  );
}
